/**
 * SSO (Single Sign-On) Service
 * Enables cross-app authentication from the Quantrolix SaaS hub
 *
 * Flow:
 * 1. User logs into quantrolix-saas
 * 2. When launching an app, we generate an SSO token
 * 3. App receives token via URL param and validates with backend
 * 4. User is authenticated in the target app without re-login
 */

import logger from '../utils/logger';
import { authService } from './auth';

const SSO_TOKEN_EXPIRY = 5 * 60 * 1000; // 5 minutes for SSO token handoff

// App configurations for SSO
export const APP_CONFIGS = {
  'fire-pos': {
    id: 'fire-pos',
    name: 'Fire POS',
    baseUrl: 'https://apps.quantrolix.ai/fire-pos',
    devUrl: 'http://localhost:3008',
    ssoEndpoint: '/auth/sso',
    icon: '\u{1F525}',
  },
  'tea-effects': {
    id: 'tea-effects',
    name: 'Tea Effects',
    baseUrl: 'https://apps.quantrolix.ai/tea-effects',
    devUrl: 'http://localhost:3003',
    ssoEndpoint: '/auth/sso',
    icon: '\u{1F375}',
  },
  'phantomcast': {
    id: 'phantomcast',
    name: 'PhantomCast',
    baseUrl: 'https://apps.quantrolix.ai/phantomcast',
    devUrl: 'http://localhost:3004',
    ssoEndpoint: '/auth/sso',
    icon: '\u{1F4FA}',
  },
  'menu-mentor': {
    id: 'menu-mentor',
    name: 'Menu Mentor',
    baseUrl: 'https://apps.quantrolix.ai/menu-flashcard',
    devUrl: 'http://localhost:3005',
    ssoEndpoint: '/auth/sso',
    icon: '\u{1F4DA}',
  },
  'bourbon-hunter': {
    id: 'bourbon-hunter',
    name: 'Bourbon Hunter',
    baseUrl: 'https://apps.quantrolix.ai/bourbon-hunter',
    devUrl: 'http://localhost:5176',
    ssoEndpoint: '/auth/sso',
    icon: '\u{1F943}',
  },
  'kasa-controller': {
    id: 'kasa-controller',
    name: 'Kasa Controller',
    baseUrl: 'https://apps.quantrolix.ai/kasa-controller',
    devUrl: 'http://localhost:3011',
    ssoEndpoint: '/auth/sso',
    icon: '\u{1F3E0}',
  },
  'yield-vault': {
    id: 'yield-vault',
    name: 'Yield Vault',
    baseUrl: 'https://apps.quantrolix.ai/yield-vault',
    devUrl: 'http://localhost:5173',
    ssoEndpoint: '/auth/sso',
    icon: '\u{1F4C8}',
  },
  'architect-app': {
    id: 'architect-app',
    name: 'Architect 3D',
    baseUrl: 'https://apps.quantrolix.ai/architect-app',
    devUrl: 'http://localhost:8010',
    ssoEndpoint: '/auth/sso',
    icon: '\u{1F4D0}',
  },
  'claude-blob': {
    id: 'claude-blob',
    name: 'Qode IDE',
    baseUrl: 'https://apps.quantrolix.ai/qode',
    devUrl: 'http://localhost:8010',
    ssoEndpoint: '/auth/sso',
    icon: '\u{1F4BB}',
  },
  'grocery-optimizer': {
    id: 'grocery-optimizer',
    name: 'Grocery Optimizer',
    baseUrl: 'https://apps.quantrolix.ai/grocery-optimizer',
    devUrl: 'http://localhost:3013',
    ssoEndpoint: '/auth/sso',
    icon: '\u{1F6D2}',
  },
  'nexus-3d-maps': {
    id: 'nexus-3d-maps',
    name: 'Nexus 3D Maps',
    baseUrl: 'https://apps.quantrolix.ai/nexus-3d',
    devUrl: 'http://localhost:5174',
    ssoEndpoint: '/auth/sso',
    icon: '\u{1F30D}',
  },
  'motionforge': {
    id: 'motionforge',
    name: 'MotionForge',
    baseUrl: 'https://apps.quantrolix.ai/motionforge',
    devUrl: 'http://localhost:3000',
    ssoEndpoint: '/auth/sso',
    icon: '\u{1F3AC}',
  },
  'api-marketplace': {
    id: 'api-marketplace',
    name: 'API Marketplace',
    baseUrl: 'https://api.quantrolix.ai',
    devUrl: 'http://localhost:3020',
    ssoEndpoint: '/auth/sso',
    icon: '\u{1F517}',
  },
};

export const ssoService = {
  /**
   * Generate an SSO token for cross-app authentication
   * @param {string} appId - Target app ID
   * @returns {Promise<string>} SSO token
   */
  async generateSSOToken(appId) {
    const baseURL = (import.meta?.env?.VITE_API_URL || 'https://quantrolix-backend-v2-production.up.railway.app').trim();
    const authToken = authService.getToken();

    if (!authToken) {
      throw new Error('User must be authenticated to generate SSO token');
    }

    try {
      const response = await fetch(`${baseURL}/api/auth/sso/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          target_app: appId,
          expires_in: SSO_TOKEN_EXPIRY,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'SSO token generation failed' }));
        throw new Error(error.detail || 'Failed to generate SSO token');
      }

      const data = await response.json();
      return data.sso_token;
    } catch (error) {
      logger.error('SSO token generation error:', error);
      // Fallback: use existing auth token with app identifier
      return this.generateLocalSSOToken(appId);
    }
  },

  /**
   * Generate a local SSO token as fallback
   * Uses the existing auth token with app metadata
   * @param {string} appId - Target app ID
   * @returns {string} Base64 encoded SSO payload
   */
  generateLocalSSOToken(appId) {
    const authToken = authService.getToken();
    const user = authService.getUser();

    if (!authToken) {
      throw new Error('User must be authenticated');
    }

    const payload = {
      token: authToken,
      user: user ? {
        email: user.email,
        name: user.name,
        plan: user.plan,
      } : null,
      target_app: appId,
      source: 'quantrolix-saas',
      issued_at: Date.now(),
      expires_at: Date.now() + SSO_TOKEN_EXPIRY,
    };

    // Base64 encode the payload
    return btoa(JSON.stringify(payload));
  },

  /**
   * Launch an app with SSO authentication
   * @param {string} appId - App ID to launch
   * @param {boolean} newWindow - Open in new window/tab
   * @returns {Promise<boolean>} Success status
   */
  async launchApp(appId, newWindow = true) {
    const appConfig = APP_CONFIGS[appId];

    if (!appConfig) {
      logger.error(`Unknown app: ${appId}`);
      return false;
    }

    try {
      // Generate SSO token
      const ssoToken = await this.generateSSOToken(appId);

      // Determine base URL (dev vs production)
      const isDev = window.location.hostname === 'localhost';
      const baseUrl = isDev ? appConfig.devUrl : appConfig.baseUrl;

      // Construct SSO URL
      const ssoUrl = new URL(baseUrl);
      ssoUrl.searchParams.set('sso_token', ssoToken);
      ssoUrl.searchParams.set('source', 'quantrolix-hub');

      // Launch app
      if (newWindow) {
        window.open(ssoUrl.toString(), '_blank');
      } else {
        window.location.href = ssoUrl.toString();
      }

      // Track app launch
      this.trackAppLaunch(appId);

      return true;
    } catch (error) {
      logger.error(`Failed to launch app ${appId}:`, error);

      // Fallback: open without SSO
      const baseUrl = window.location.hostname === 'localhost'
        ? appConfig.devUrl
        : appConfig.baseUrl;
      window.open(baseUrl, '_blank');

      return false;
    }
  },

  /**
   * Track app launch for analytics
   * @param {string} appId - App that was launched
   */
  trackAppLaunch(appId) {
    const baseURL = (import.meta?.env?.VITE_API_URL || 'https://quantrolix-backend-v2-production.up.railway.app').trim();
    const authToken = authService.getToken();

    // Fire and forget analytics
    fetch(`${baseURL}/api/analytics/app-launch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authToken ? `Bearer ${authToken}` : '',
      },
      body: JSON.stringify({
        app_id: appId,
        timestamp: new Date().toISOString(),
        source: 'marketplace',
      }),
    }).catch(() => {
      // Ignore analytics errors
    });

    // Store locally for quick access
    const recentApps = JSON.parse(localStorage.getItem('recent_apps') || '[]');
    const updated = [appId, ...recentApps.filter(a => a !== appId)].slice(0, 5);
    localStorage.setItem('recent_apps', JSON.stringify(updated));
  },

  /**
   * Get recently launched apps
   * @returns {string[]} Array of recent app IDs
   */
  getRecentApps() {
    return JSON.parse(localStorage.getItem('recent_apps') || '[]');
  },

  /**
   * Get app config by ID
   * @param {string} appId - App ID
   * @returns {object|null} App configuration
   */
  getAppConfig(appId) {
    return APP_CONFIGS[appId] || null;
  },

  /**
   * Get all app configurations
   * @returns {object} All app configs
   */
  getAllApps() {
    return APP_CONFIGS;
  },

  /**
   * Validate incoming SSO token (for apps receiving SSO auth)
   * @param {string} ssoToken - SSO token to validate
   * @returns {Promise<object|null>} User data if valid, null otherwise
   */
  async validateSSOToken(ssoToken) {
    const baseURL = (import.meta?.env?.VITE_API_URL || 'https://quantrolix-backend-v2-production.up.railway.app').trim();

    try {
      const response = await fetch(`${baseURL}/api/auth/sso/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sso_token: ssoToken }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      // If valid, store the auth data
      if (data.valid && data.access_token) {
        authService.setAuthData(data.access_token, data.user);
        return data.user;
      }

      return null;
    } catch (error) {
      logger.error('SSO validation error:', error);

      // Try local validation as fallback
      return this.validateLocalSSOToken(ssoToken);
    }
  },

  /**
   * Validate a locally-generated SSO token
   * @param {string} ssoToken - Base64 encoded SSO payload
   * @returns {object|null} User data if valid
   */
  validateLocalSSOToken(ssoToken) {
    try {
      const payload = JSON.parse(atob(ssoToken));

      // Check expiration
      if (payload.expires_at < Date.now()) {
        logger.warn('SSO token expired');
        return null;
      }

      // Check source
      if (payload.source !== 'quantrolix-saas') {
        logger.warn('Invalid SSO token source');
        return null;
      }

      // Store auth data
      if (payload.token) {
        authService.setAuthData(payload.token, payload.user);
        return payload.user;
      }

      return null;
    } catch (error) {
      logger.error('Local SSO validation error:', error);
      return null;
    }
  },

  /**
   * Handle SSO callback on app load
   * Called when an app receives an SSO token via URL
   * @returns {Promise<boolean>} True if SSO was successful
   */
  async handleSSOCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const ssoToken = urlParams.get('sso_token');

    if (!ssoToken) {
      return false;
    }

    const user = await this.validateSSOToken(ssoToken);

    if (user) {
      // Clean up URL
      urlParams.delete('sso_token');
      urlParams.delete('source');
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      return true;
    }

    return false;
  },
};

export default ssoService;
